import React, { useState } from 'react';
import { Gift, Check, AlertCircle, ChevronDown } from 'lucide-react';
import { tagReferrerKNumber } from '@/components/checkoutRedirect';

const K_REGEX = /^K\d{7}$/i;
const isValid = (v) => K_REGEX.test((v || '').trim().toUpperCase());

/**
 * Optional self-report field on checkout: "Blev du henvist af en eksisterende kunde?"
 * User enters the referrer's K-number. On confirm we embed it in the b44 click ID
 * (_hvm_<KNUMBER>) via tagReferrerKNumber, so it travels through the external checkout
 * and gets attributed even if the last click came from Google/direct/another device.
 */
export default function ReferralKNumberField() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');

  const sanitize = (raw) => raw.toUpperCase().replace(/[^K0-9]/g, '').slice(0, 8);

  const handleConfirm = () => {
    if (!isValid(value)) {
      setError('Kundenummer skal være K efterfulgt af 7 cifre (f.eks. K1237891)');
      return;
    }
    tagReferrerKNumber(value);
    setConfirmed(true);
    setError('');
  };

  if (confirmed) {
    return (
      <div className="bg-[#f0faf5] border border-[#1a3a32]/20 rounded-2xl px-4 py-3.5 flex items-center gap-3">
        <div className="w-8 h-8 bg-[#1a3a32] rounded-full flex items-center justify-center flex-shrink-0">
          <Check className="w-4 h-4 text-white" strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#1a3a32]">Tak! Vi har registreret din henvisning</p>
          <p className="text-xs text-gray-500">Henvist af <span className="font-mono font-bold">{value.toUpperCase()}</span> — de får æren for din tilmelding</p>
        </div>
        <button onClick={() => { setConfirmed(false); setValue(''); }} className="text-xs text-gray-400 hover:text-gray-600 flex-shrink-0">Ret</button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors">
        <div className="w-8 h-8 bg-[#F6F3EC] rounded-lg flex items-center justify-center flex-shrink-0">
          <Gift className="w-4 h-4 text-[#B58716]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Blev du henvist af en kunde?</p>
          <p className="text-xs text-gray-400 mt-0.5">Indtast deres kundenummer, så de får deres henvisningsbonus</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-50">
          <p className="text-xs text-gray-500 leading-relaxed mb-3">
            Hvis en eksisterende enkel-kunde har anbefalet os, kan du skrive deres kundenummer her (starter med K og har 7 cifre). Så sikrer vi, at de får deres henvisningsbonus — også selvom du fandt os på en anden måde.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={value}
              onChange={e => { setValue(sanitize(e.target.value)); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleConfirm()}
              placeholder="K1237891"
              className="flex-1 px-3 py-2.5 text-sm font-mono tracking-wider border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#1a3a32] bg-white"
            />
            <button
              onClick={handleConfirm}
              disabled={!value}
              className="px-4 py-2.5 bg-[#1a3a32] hover:bg-[#0e2a1c] disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-semibold rounded-xl transition-colors flex-shrink-0">
              Bekræft
            </button>
          </div>
          {error && (
            <p className="text-red-500 text-xs flex items-center gap-1 mt-2">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}